import * as React from 'react';
import type { LinkProps } from '@tanstack/react-router';
import { Link, useMatchRoute, useNavigate } from '@tanstack/react-router';

import { cn } from '../../../lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../ui/select';

// ─── Constants ────────────────────────────────────────────────────────────────

const ACTIVE_TAB_CLASSES = 'bg-background text-foreground shadow-sm';
const BASE_TAB_CLASSES = 'inline-flex shrink-0 items-center whitespace-nowrap rounded-md transition-colors';
const INACTIVE_TAB_CLASSES = 'text-muted-foreground hover:text-foreground';

const SIZE_CLASSES = {
  default: 'px-4 py-2 text-sm gap-2',
  sm: 'px-3 py-1.5 text-xs gap-1.5',
} as const;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RouteTab {
  /** TanStack Router activeOptions forwarded to Link */
  activeOptions?: LinkProps['activeOptions'];
  /** Icon rendered before the label */
  icon: React.ReactNode;
  /** Tab label text */
  label: string;
  /** Route params to pass to the Link (e.g. { tenantId: '123' }) */
  params?: Record<string, string>;
  /** Permission key required to display this tab. Tabs without this field are always shown. */
  permission?: string;
  /** Route path to navigate to (e.g. '/tenants/$tenantId') */
  to: string;
}

export interface RouteTabsProps {
  /** Accessible label for the nav element */
  ariaLabel: string;
  /** Permission checker function — if omitted, all tabs are shown */
  hasPermission?: (permission: string) => boolean;
  /** Size variant for tabs */
  size?: 'default' | 'sm';
  /** Array of tab definitions to render */
  tabs: RouteTab[];
}

// ─── Component ────────────────────────────────────────────────────────────────

export function RouteTabs({
  ariaLabel,
  hasPermission,
  size = 'default',
  tabs,
}: RouteTabsProps): React.JSX.Element | null {
  const tabClasses = cn(BASE_TAB_CLASSES, SIZE_CLASSES[size]);
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();

  const visibleTabs = tabs.filter((tab) => {
    if (tab.permission === undefined) return true;
    if (hasPermission === undefined) return true;
    return hasPermission(tab.permission);
  });

  if (visibleTabs.length === 0) return null;

  const activeTab = visibleTabs.find((tab) =>
    matchRoute({ fuzzy: !tab.activeOptions?.exact, params: tab.params as Record<string, string>, to: tab.to }),
  );

  const handleSelectChange = (value: string): void => {
    const selectedTab = visibleTabs.find((tab) => tab.to === value);
    if (selectedTab) {
      void navigate({ params: selectedTab.params, to: selectedTab.to });
    }
  };

  return (
    <>
      {/* Mobile: dropdown select */}
      <div className="md:hidden">
        <Select onValueChange={handleSelectChange} value={activeTab?.to ?? ''}>
          <SelectTrigger aria-label={ariaLabel} className="w-full [&>span]:flex [&>span]:items-center [&>span]:gap-2">
            <span>
              {activeTab?.icon}
              {activeTab?.label}
            </span>
          </SelectTrigger>
          <SelectContent>
            {visibleTabs.map((tab) => (
              <SelectItem key={tab.to} value={tab.to}>
                <span className="flex items-center gap-2">
                  {tab.icon}
                  {tab.label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Desktop: horizontal tab bar */}
      <nav
        aria-label={ariaLabel}
        className="hidden md:inline-flex overflow-x-auto items-center gap-1 rounded-lg bg-muted p-1"
      >
        {visibleTabs.map((tab) => (
          <Link
            key={tab.to}
            activeOptions={tab.activeOptions}
            activeProps={{ className: cn(tabClasses, ACTIVE_TAB_CLASSES) }}
            inactiveProps={{ className: cn(tabClasses, INACTIVE_TAB_CLASSES) }}
            params={tab.params}
            to={tab.to}
          >
            {tab.icon}
            {tab.label}
          </Link>
        ))}
      </nav>
    </>
  );
}
